import React, { useMemo } from 'react';
import * as mobxReact from 'mobx-react';
import VisibilitySensor from 'react-visibility-sensor';
import { IControlMeetingDetailsDTO } from '../../../../../../api/control.dao';
import { getSortedMeetingsByPresenterName } from '../../../../../../infrastructure/utils/sorting.utils';
import { ListDayTimeItem } from '../../../shared/list-day-item/list-day-time-item';
import ControlViewModel from '../../control.viewmodel';
import { ControlListItem } from '../_control-list-item/_control-list-item';
import { HEADER_HEIGHT } from '../../../../../../constants/common.constants';
import { getProgressList, IMeetingsGroup, invokeScrollEvent } from '../../../../../../infrastructure/utils/common.utils';
import { MeetingProgressLabel, MeetingProgressList } from '../../../../../../constants/meeting.constants';
import { ControlListGroupHeader } from './_control-list-group-header/_control-list-group-header';

export interface IProps {
    timeZone: string;
    colSpan: number;
    group: IMeetingsGroup;
    controlVM: ControlViewModel;
    groupHeaderRef?: React.RefObject<HTMLElement>;
    showColumn: (id) => boolean;
}

const ControlListGroupItem = mobxReact.observer((props: IProps) => {
    const {
        group,
        timeZone,
        colSpan,
        controlVM,
        groupHeaderRef,
        showColumn
    } = props;

    const {
        collapsedMeetingIds,
        collapsedGroups,
        collapsedHiddenGroups,
        onCollapseMeetings,
        onExpandMeetings,
        onCollapseGroup,
        onExpandGroup,
    } = controlVM;

    const {
        startDateTime,
        endDateTime,
        meetings,
        hiddenMeetings
    } = group;

    const meetingTimeGroup = { startDateTime: startDateTime.toString(), endDateTime: endDateTime.toString() };
    const isGroupCollapsed = collapsedGroups.some(el =>
        el.startDateTime === meetingTimeGroup.startDateTime && el.endDateTime === meetingTimeGroup.endDateTime
    );

    const isHiddenGroupCollapsed = collapsedHiddenGroups.some(el =>
        el.startDateTime === meetingTimeGroup.startDateTime && el.endDateTime === meetingTimeGroup.endDateTime
    );

    const groupMeetingIds = [...meetings, ...hiddenMeetings].map(el => el.meetingId);
    const groupVisibleMeetingIds = meetings.map(el => el.meetingId);
    const groupHiddenMeetingIds = hiddenMeetings.map(el => el.meetingId);

    const isAllMeetingsCollapsed = groupMeetingIds.every(id => collapsedMeetingIds.includes(id));
    const isAllHiddenMeetingsCollapsed = groupHiddenMeetingIds.every(id => collapsedMeetingIds.includes(id));

    const handleCollapseGroupToggle = () => {
        if (!isAllMeetingsCollapsed && !isGroupCollapsed) {
            onCollapseMeetings(groupMeetingIds);
        } else {
            if (isGroupCollapsed) {
                onExpandGroup(meetingTimeGroup);
                onExpandMeetings(groupVisibleMeetingIds);
            } else {
                onCollapseGroup(meetingTimeGroup);
            }
        }
        // Invoke scroll event to check items visibility
        invokeScrollEvent();
    };

    const handleCollapseHiddenGroupToggle = () => {
        if (!isAllHiddenMeetingsCollapsed && !isHiddenGroupCollapsed) {
            onCollapseMeetings(groupHiddenMeetingIds);
        } else {
            if (isHiddenGroupCollapsed) {
                onExpandGroup(meetingTimeGroup, true);
                onExpandMeetings(groupHiddenMeetingIds);
            } else {
                onCollapseGroup(meetingTimeGroup, true);
            }
        }
        // Invoke scroll event to check items visibility
        invokeScrollEvent();
    };

    const handleCollapseMeetingToggle = meetingId => {
        const isMeetingCollapsed = collapsedMeetingIds.includes(meetingId);

        if (isMeetingCollapsed) {
            onExpandMeetings([meetingId]);
        } else {
            onCollapseMeetings([meetingId]);
        }
        // Invoke scroll event to check items visibility
        invokeScrollEvent();
    };

    const sortedMeetings = useMemo(() => getSortedMeetingsByPresenterName<IControlMeetingDetailsDTO>(meetings), [meetings]);
    const sortedHiddenMeetings = useMemo(() => getSortedMeetingsByPresenterName<IControlMeetingDetailsDTO>(hiddenMeetings), [hiddenMeetings]);
    const progressList = getProgressList(controlVM.selectedFilterStatuses);

    const getSectionTitle = (visible: boolean): string => {
        let result = [];

        if (visible) {
            result = MeetingProgressList.filter(el => progressList.includes(el));
        } else {
            result = MeetingProgressList.filter(el => !progressList.includes(el));
        }

        return result.map(status => MeetingProgressLabel[status]).join(', ');
    };

    return (
        <>
            <ListDayTimeItem
                startDateTime={startDateTime}
                endDateTime={endDateTime}
                eventTimezone={timeZone}
                colSpan={colSpan}
                onCollapseToggle={handleCollapseGroupToggle}
                isCollapsed={isGroupCollapsed}
                isAllMeetingsCollapsed={isAllMeetingsCollapsed}
                withCollapse
                groupHeaderRef={groupHeaderRef}
                title={getSectionTitle(true)}
            />
            { !isGroupCollapsed && sortedMeetings.map((meeting: IControlMeetingDetailsDTO, index: number) => (
                <VisibilitySensor
                    key={meeting.meetingId}
                    partialVisibility
                    offset={{ top: HEADER_HEIGHT }}
                    intervalCheck={false}
                    scrollCheck
                    scrollDelay={1000}
                    resizeCheck
                    resizeDelay={1000}
                    >
                    {({ isVisible }) => {
                        return <ControlListItem
                            eventTimezone={timeZone}
                            onCollapseToggle={handleCollapseMeetingToggle}
                            isCollapsed={collapsedMeetingIds.includes(meeting.meetingId)}
                            isFirstInTimeGroup={index === 0}
                            isVisible={isVisible}
                            controlVM={controlVM}
                            groupedOperators={controlVM.groupedOperatorsMap[meeting.meetingId]}
                            showColumn={showColumn}
                            {...meeting}
                        />;
                    }}
                </VisibilitySensor>
            ))}
            {!isGroupCollapsed && !!sortedHiddenMeetings.length && (
                <>
                    <ControlListGroupHeader
                        colSpan={colSpan}
                        title={getSectionTitle(false)}
                        onCollapseToggle={handleCollapseHiddenGroupToggle}
                        isCollapsed={isHiddenGroupCollapsed}
                        isAllMeetingsCollapsed={isAllHiddenMeetingsCollapsed}
                    />
                    { !isHiddenGroupCollapsed && sortedHiddenMeetings.map((meeting: IControlMeetingDetailsDTO, index: number) => (
                        <VisibilitySensor
                            key={meeting.meetingId}
                            partialVisibility
                            offset={{ top: HEADER_HEIGHT }}
                            intervalCheck={false}
                            scrollCheck
                            scrollDelay={1000}
                            resizeCheck
                            resizeDelay={1000}
                            >
                            {({ isVisible }) => {
                                return <ControlListItem
                                    onCollapseToggle={handleCollapseMeetingToggle}
                                    isCollapsed={collapsedMeetingIds.includes(meeting.meetingId)}
                                    isFirstInTimeGroup={index === 0}
                                    isVisible={isVisible}
                                    controlVM={controlVM}
                                    eventTimezone={timeZone}
                                    groupedOperators={controlVM.groupedOperatorsMap[meeting.meetingId]}
                                    showColumn={showColumn}
                                    {...meeting}
                                />;
                            }}
                        </VisibilitySensor>
                    ))}
                </>
            )}
        </>
    );
});

export default ControlListGroupItem;
